import { useState, useEffect } from 'react'
import {
	getGenders,
	getDocumentTypes,
	getHealthInsurances,
	getHealthInsurancePlans as fetchHealthInsurancePlans,
	signUp
} from '../../src/api'

const API_TIMEOUT = 10000

const promiseWithTimeout = (promise, timeout) => {
	return Promise.race([
		promise,
		new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), timeout))
	])
}

export const useSignUpData = () => {
	const [genders, setGenders] = useState([])
	const [documentTypes, setDocumentTypes] = useState([])
	const [healthInsurances, setHealthInsurances] = useState([])
	const [plans, setPlans] = useState([])
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState(null)

	useEffect(() => {
		const fetchInitialData = async () => {
			try {
				setLoading(true)
				setError(null)

				const apiCallsWithTimeout = promiseWithTimeout(
					Promise.all([getGenders(), getDocumentTypes(), getHealthInsurances()]),
					API_TIMEOUT
				)

				const [gendersRes, documentTypesRes, healthInsurancesRes] = await apiCallsWithTimeout

				if (gendersRes?.data) setGenders(gendersRes.data)
				if (documentTypesRes?.data) setDocumentTypes(documentTypesRes.data)
				if (healthInsurancesRes?.data) setHealthInsurances(healthInsurancesRes.data)
			} catch (err) {
				console.error('Error al cargar datos desde API:', err)
				setError(err)
			} finally {
				setLoading(false)
			}
		}

		fetchInitialData()
	}, [])

	const getHealthInsurancePlans = async healthInsuranceId => {
		if (!healthInsuranceId) {
			setPlans([])
			return
		}

		try {
			const response = await promiseWithTimeout(
				fetchHealthInsurancePlans(healthInsuranceId),
				API_TIMEOUT
			)

			if (response?.data) {
				setPlans(response.data)
			} else {
				setPlans([])
			}
		} catch (err) {
			console.warn('hubo un error al cargar los planes:', err)
		}
	}

	return {
		genders,
		documentTypes,
		healthInsurances,
		plans,
		loading,
		error,
		getHealthInsurancePlans
	}
}

export const useSignUpSubmit = () => {
	const [submitting, setSubmitting] = useState(false)
	const [success, setSuccess] = useState(false)
	const [error, setError] = useState(null)

	const handleSubmit = async values => {
		setSubmitting(true)
		setError(null)

		try {
			const params = {
				mail: values.email,
				nombres: values.name,
				apellidos: values.lastName,
				tipoDocumento: values.documentType,
				documento: values.documentNumber,
				sexo: values.gender,
				dob: values.birthDate,
				nroAfiliado: values.credentialNumber,
				obraSocial: values.healthInsurance,
				plan: values.plan
			}

			await promiseWithTimeout(signUp(params), API_TIMEOUT)
			setSuccess(true)
			return { success: true }
		} catch (err) {
			setError(err)
			return { success: false, error: err }
		} finally {
			setSubmitting(false)
		}
	}

	const reset = () => {
		setSubmitting(false)
		setSuccess(false)
		setError(null)
	}

	return {
		handleSubmit,
		submitting,
		success,
		error,
		reset
	}
}
